import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../../contexts/AuthContext";
import { FlightCard } from "./FlightCard";
import { AddFlightForm } from "./AddFlightForm";

const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const LORA  = { fontFamily: "'Lora', Georgia, serif" };

function SkeletonCard() {
  return (
    <div className="border border-[#DCD4C6] rounded-sm animate-pulse overflow-hidden">
      <div className="p-4 bg-[#F9F6F0] space-y-2">
        <div className="h-3 bg-[#DCD4C6] rounded w-24" />
        <div className="h-7 bg-[#DCD4C6] rounded w-20" />
        <div className="h-4 bg-[#DCD4C6] rounded w-full mt-3" />
      </div>
      <div className="p-4 space-y-2 border-t border-[#DCD4C6]">
        <div className="h-3 bg-[#DCD4C6] rounded w-1/2" />
        <div className="h-3 bg-[#DCD4C6] rounded w-1/3" />
      </div>
    </div>
  );
}

export function FlightTrackerSection({ tripId, defaultDate }) {
  const [flights,  setFlights]  = useState([]);
  const [loading,  setLoading]  = useState(true);

  const loadFlights = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/trips/${tripId}/flights`);
      setFlights(data);
    } catch (err) {
      // Silently fail — affiliation links still visible below
      console.warn("[FlightTracker] Could not load flights:", err);
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => { loadFlights(); }, [loadFlights]);

  const handleAdded   = (newFlight) => setFlights((prev) => [...prev, newFlight]);
  const handleRefreshed = (updated) =>
    setFlights((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
  const handleDeleted = (id) =>
    setFlights((prev) => prev.filter((f) => f.id !== id));

  if (loading) {
    return (
      <div className="space-y-4 mb-6">
        <SkeletonCard />
      </div>
    );
  }

  return (
    <div className="space-y-4 mb-6" data-testid="flight-tracker-section">
      {/* Existing flights */}
      {flights.length > 0 && (
        <div className="space-y-3">
          {flights.map((flight) => (
            <FlightCard
              key={flight.id}
              flight={flight}
              tripId={tripId}
              onRefreshed={handleRefreshed}
              onDeleted={handleDeleted}
            />
          ))}
        </div>
      )}

      {/* Add flight form — always visible (unless max 4 reached) */}
      <AddFlightForm
        tripId={tripId}
        defaultDate={defaultDate}
        onAdded={handleAdded}
        disabled={flights.length >= 4}
      />
    </div>
  );
}
